import React, { useState, useEffect } from 'react';
import { injectIntl, FormattedMessage } from 'react-intl';
import { SVGIcon } from '@superops/super-icons';
import { useQuery } from '@apollo/client';
import { isEmpty } from 'lodash';

import { DromoUploader, getTranslatedText } from '@nile/components';
import { AccountClient, GET_SERVICE_CATALOG_ITEMS } from '@nile/graphql';
import { GET_CLIENT_PROVISION_RULE_LIST } from '@nile/graphql/src/Query/contracts';
import Loader from '@nile/mangifera/src/Components/Ticket/TicketCount';

import {
  getClientContractImportFields,
  getQuery,
  callQuery,
  shapeOptions
} from '../Fields/clientContractV3Fields';
import { intlConverter } from '../utils/helpers';

const ClientContractV3Uploader = ({
  onResults,
  onCancel,
  validationProps = {},
  fields: propFields,
  userToken,
  intl,
  activator
}) => {
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [fields, setFields] = useState([]);
  const [serviceCatalogItems, setServiceCatalogItems] = useState([]);

  useQuery(GET_SERVICE_CATALOG_ITEMS, {
    client: AccountClient,
    variables: {
      listInfo: {
        condition: {
          joinOperator: 'AND',
          operands: [
            {
              attribute: 'status',
              operator: 'is',
              value: 'ACTIVE'
            }
          ]
        }
      }
    },
    onCompleted(res) {
      if (res?.getServiceCatalogItems?.items) {
        setServiceCatalogItems(res.getServiceCatalogItems.items);
      }
    }
  });

  const validationFieldsArray = [];

  const handleRowHooks = async ({ row, index }) => {
    const newRowValue = { row, index };

    validationFieldsArray.forEach((key) => {
      newRowValue.row[key].info = [];
    });

    function addRequiredError(key) {
      if (!validationFieldsArray.includes(key)) {
        validationFieldsArray.push(key);
      }
      newRowValue.row[key].info = [
        {
          message: getTranslatedText(intl, 'dromo.validation.requiredField'),
          level: 'error'
        }
      ];
    }

    //service items filter based on contract type
    if (
      newRowValue.row.contractType.resultValue === 'SERVICE' &&
      !newRowValue.row.chargeItemId.info.message
    ) {
      newRowValue.row.chargeItemId.selectOptions = serviceCatalogItems
        .filter(
          (item) =>
            item?.itemType === 'SERVICE' &&
            ['BLOCK_HOUR', 'BLOCK_MONEY', 'SERVICE_BUNDLE'].includes(
              item?.serviceTypeItem?.offeringType
            )
        )
        .map((el) => {
          return {
            value: el?.itemId,
            label: el?.name
          };
        });
    }
    if (
      newRowValue.row.contractType.resultValue === 'USAGE' &&
      !newRowValue.row.chargeItemId.info.message
    ) {
      newRowValue.row.chargeItemId.selectOptions = serviceCatalogItems
        .filter(
          (item) =>
            (item?.itemType === 'SERVICE' &&
              ['QUANTITY'].includes(item?.serviceTypeItem?.offeringType)) ||
            item?.itemType === 'PRODUCT'
        )
        .map((el) => {
          return {
            value: el?.itemId,
            label: el?.name
          };
        });
    }

    //Selling Price Required check
    if (
      newRowValue.row.sellingPriceOverridden.resultValue === 'true' &&
      !newRowValue.row.sellingPrice.resultValue
    ) {
      addRequiredError('sellingPrice');
    }

    //Site Validation for specific site
    if (
      newRowValue.row.billableSiteType.resultValue === 'INCLUDED' &&
      isEmpty(newRowValue.row.addSites.resultValue)
    ) {
      addRequiredError('addSites');
    }

    //quanity calculation type update select option
    if (newRowValue.row.sellingPriceCalculationType.resultValue === 'DYNAMIC') {
      newRowValue.row.quantityCalculationType.selectOptions = [
        {
          label: 'Fixed',
          value: 'FIXED'
        }
      ];
    } else if (
      newRowValue.row.sellingPriceCalculationType.resultValue === 'FIXED'
    ) {
      newRowValue.row.quantityCalculationType.selectOptions = [
        {
          label: 'Fixed',
          value: 'FIXED'
        },
        {
          label: 'Dynamic',
          value: 'DYNAMIC'
        }
      ];
    }

    //selling price calculation type update select option
    if (newRowValue.row.quantityCalculationType.resultValue === 'DYNAMIC') {
      newRowValue.row.sellingPriceCalculationType.selectOptions = [
        {
          label: 'Fixed',
          value: 'FIXED'
        }
      ];
    } else if (
      newRowValue.row.quantityCalculationType.resultValue === 'FIXED'
    ) {
      newRowValue.row.sellingPriceCalculationType.selectOptions = [
        {
          label: 'Fixed',
          value: 'FIXED'
        },
        {
          label: 'Dynamic',
          value: 'DYNAMIC'
        }
      ];
    }

    //quanity field required check
    if (
      newRowValue.row.sellingPriceCalculationType.resultValue === 'FIXED' &&
      newRowValue.row.quantityCalculationType.resultValue === 'FIXED'
    ) {
      if (!newRowValue.row.quantity.value) {
        addRequiredError('quantity');
      }
    }

    if (
      newRowValue.row.sellingPriceCalculationType.resultValue === 'FIXED' &&
      newRowValue.row.quantityCalculationType.resultValue === 'DYNAMIC'
    ) {
      if (!newRowValue.row.provisionRuleId.value) {
        addRequiredError('provisionRuleId');
      }

      //provision rule filter from api
      if (newRowValue.row?.clientId?.resultValue) {
        try {
          const res = await AccountClient.query({
            query: GET_CLIENT_PROVISION_RULE_LIST,
            variables: {
              input: {
                accountId: newRowValue.row.clientId.resultValue
              }
            }
          });

          let provisionRules =
            res?.data?.getClientProvisionRuleList?.provisionRules || [];

          // Filter and map rules
          const options = provisionRules
            .filter((el) => el?.ruleEntityType === 'CLIENT' || el?.parentRule)
            .map((rule) => ({
              value: rule?.provisionRuleId,
              label: getTranslatedText(intl, rule?.name)
            }));
          newRowValue.row.provisionRuleId.selectOptions = options;
        } catch (e) {
          newRowValue.row.provisionRuleId.info.message = e;
        }
      }
    }

    // Frequency Type if recurring check required for frequencyDurationUnit, recurringMode, frequencyInterval
    if (newRowValue.row.frequencyType.resultValue === 'RECURRING') {
      if (isEmpty(newRowValue.row.frequencyDurationUnit.resultValue))
        addRequiredError('frequencyDurationUnit');

      if (isEmpty(newRowValue.row.recurringMode.resultValue)) {
        addRequiredError('recurringMode');
      }

      if (!newRowValue.row.frequencyInterval.resultValue) {
        addRequiredError('frequencyInterval');
      }
    }

    //FrequencyInterval value range check
    const frequencyIntervalMap = {
      DAY: 31,
      MONTH: 12,
      WEEK: 4,
      YEAR: 31
    };

    if (
      newRowValue.row.frequencyInterval.resultValue >
      frequencyIntervalMap[newRowValue.row.frequencyDurationUnit.resultValue]
    ) {
      newRowValue.row.frequencyInterval.info = [
        {
          message: getTranslatedText(
            intl,
            'dromo.validation.frequencyQuantity.invalidOption'
          ),
          level: 'error'
        }
      ];
    }

    const serviceItem = serviceCatalogItems.find(
      (serItem) => serItem.itemId === newRowValue.row?.chargeItemId?.resultValue
    );

    if (serviceItem) {
      //frequencyType validation based on offeringType and itemType
      if (
        serviceItem?.itemType === 'PRODUCT' ||
        serviceItem?.serviceTypeItem?.offeringType === 'QUANTITY' ||
        serviceItem?.serviceTypeItem?.offeringType === 'HOURS'
      ) {
        newRowValue.row.frequencyType.selectOptions = [
          {
            label: 'Recurring',
            value: 'RECURRING'
          }
        ];
      }

      if (
        serviceItem?.serviceTypeItem?.offeringType === 'BLOCK_HOUR' ||
        serviceItem?.serviceTypeItem?.offeringType === 'BLOCK_MONEY'
      ) {
        newRowValue.row.frequencyType.selectOptions = [
          {
            label: 'Perpetual',
            value: 'PERPETUAL'
          },
          {
            label: 'Recurring',
            value: 'RECURRING'
          }
        ];
        newRowValue.row.recurringMode.selectOptions = [
          {
            label: 'Upfront',
            value: 'UPFRONT'
          },
          {
            label: 'Advance',
            value: 'ADVANCE'
          }
        ];
        newRowValue.row.quantityCalculationType.selectOptions = [
          {
            label: 'Fixed',
            value: 'FIXED'
          }
        ];
        newRowValue.row.sellingPriceCalculationType.selectOptions = [
          {
            label: 'Fixed',
            value: 'FIXED'
          }
        ];
      } else {
        newRowValue.row.frequencyType.selectOptions = [
          {
            label: 'Recurring',
            value: 'RECURRING'
          }
        ];
        newRowValue.row.recurringMode.selectOptions = [
          {
            label: 'Arrear',
            value: 'ARREAR'
          },
          {
            label: 'Upfront',
            value: 'UPFRONT'
          },
          {
            label: 'Advance',
            value: 'ADVANCE'
          }
        ];
      }
    }

    //add description for addSites field if specific site in billableSiteType
    // if (newRowValue.row.billableSiteType.resultValue === 'INCLUDED') {
    //   newRowValue.row.addSites.description =
    //     'Please add value of sites with ; seperator';
    // }

    return newRowValue;
  };

  useEffect(() => {
    if (!propFields) {
      setLoading(true);
      const clientContractV3ImportFields = getClientContractImportFields(intl);

      const initFields = [...clientContractV3ImportFields];

      const selectFields = clientContractV3ImportFields.reduce(
        (acc, field, idx) => {
          if (field.type === 'select')
            acc[idx] = getQuery(field.key)
              ? callQuery(field.key)
              : field.selectOptions;
          return acc;
        },
        {}
      );

      const promises = Object.values(selectFields);
      Promise.all(promises)
        .then((options) => {
          options.forEach((set, idx) => {
            const indexToModify = Object.keys(selectFields)[idx];
            initFields[indexToModify].selectOptions = shapeOptions(
              initFields[indexToModify].key,
              set
            );
          });
          setFields([...intlConverter(intl, initFields)]);
          setLoading(false);
        })
        .catch((e) => {
          setLoading(false);
          setError(true);
          console.log(e);
        });
    }
  }, []);

  const handleReviewStep = (instance, data) => {
    const availableFields = Object.keys(data?.fields);

    const clientContractV3ImportFields = getClientContractImportFields(intl);

    const fieldsToAdd = clientContractV3ImportFields?.filter(
      (field) =>
        !('validators' in field) && !availableFields.includes(field.key)
    );
    if (fieldsToAdd.length) {
      const fieldsToAddIntl = intlConverter(intl, fieldsToAdd);
      fieldsToAddIntl.forEach((field) => {
        instance.addField({ ...field, type: field?.type || 'string' });
      });
    }
  };

  if (loading) return <Loader loading />;

  if (error) return null;

  return (
    <DromoUploader
      settings={{
        importIdentifier: 'CONTRACT',
        title: getTranslatedText(intl, 'dromo.clientContract.import.title')
      }}
      fields={propFields || fields}
      rowHooks={[(row) => row, handleRowHooks]}
      columnHooks={[(column) => column]}
      stepHooks={[{ type: 'reviewStep', callback: handleReviewStep }]}
      onResults={(response, metadata) => {
        setResults(response);
        if (onResults) onResults();
      }}
      onCancel={() => {
        if (onCancel) onCancel();
      }}
      activator={
        activator || (
          <div className="flex center actionListItemPadding ptr">
            <div className="mr-10 flex center">
              <SVGIcon name="Requesters" />
            </div>
            <FormattedMessage id="dromo.csv.clientContract.import" />
          </div>
        )
      }
      userToken={userToken}
      backendSync
    />
  );
};

export default injectIntl(ClientContractV3Uploader);
